"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useRef, useSyncExternalStore } from "react";
import { ServicesCard, Container } from "@/components";
import { ServicesData } from "@/data/ServicesData";
import { motion } from "framer-motion";
const subscribeToViewport = (onStoreChange: () => void) => {
  window.addEventListener("resize", onStoreChange);
  return () => window.removeEventListener("resize", onStoreChange);
};

// How much scrolling one slide costs, as a fraction of the natural
// one-viewport-per-slide pace. Halved because the original pace meant a long
// stretch of wheel just to move between two cards.
const SLIDE_SCROLL_PACE = 0.5;
const getSectionHeight = () => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const totalSlides = ServicesData.length;
  const initialMargin = vw >= 1024 ? 40 : 20;
  const slideDistance = (totalSlides - 1) * vw + initialMargin;
  const stayBuffer = vw < 768 ? 200 : 400;
  // Only the scrollable part is scaled — the sticky viewport still needs its
  // own full `vh` on top, or the last slide never finishes.
  return (slideDistance + stayBuffer) * SLIDE_SCROLL_PACE + vh;
};
const getServerSectionHeight = () => 0;
const Services = () => {
  const $ = _c(21);
  const dynamicHeight = useSyncExternalStore(subscribeToViewport, getSectionHeight, getServerSectionHeight);
  const [activeIndex, setActiveIndex] = useState(0);
  const [globalProgress, setGlobalProgress] = useState(0);
  const containerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== dynamicHeight) {
    t0 = () => {
      let frame = 0;
      const measure = () => {
        frame = 0;
        if (!containerRef.current) {
          return;
        }
        const offsetTop = containerRef.current.offsetTop;
        const scrolled = window.scrollY;
        const vh = window.innerHeight;
        const delta = scrolled - offsetTop;
        const totalScrollableArea = dynamicHeight - vh;
        let progress = delta / totalScrollableArea;
        if (progress < 0) {
          progress = 0;
        }
        if (progress > 1) {
          progress = 1;
        }
        setGlobalProgress(progress);
        const numSlides = ServicesData.length;
        const index = Math.min(Math.floor(progress * numSlides), ServicesData.length - 1);
        setActiveIndex(index);
      };
      const handleScroll = () => {
        if (frame) {
          return;
        }
        frame = requestAnimationFrame(measure);
      };
      handleScroll();
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", handleScroll);
      };
    };
    t1 = [dynamicHeight];
    $[0] = dynamicHeight;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== dynamicHeight) {
    t2 = dynamicHeight ? {
      height: `${dynamicHeight}px`
    } : undefined;
    $[3] = dynamicHeight;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="flex-1" aria-hidden={true} />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== activeIndex) {
    t4 = <div className="relative z-10 w-full"><ServicesCard activeIndex={activeIndex} /></div>;
    $[6] = activeIndex;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex-1" aria-hidden={true} />;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== activeIndex || $[10] !== globalProgress) {
    t6 = ServicesData.map((service, idx) => {
      const Icon = service.icon;
      const segmentSize = 1 / ServicesData.length;
      const segmentStart = idx * segmentSize;
      let currentFill = (globalProgress - segmentStart) / segmentSize;
      currentFill = Math.min(Math.max(currentFill, 0), 1);
      const finalPercent = currentFill;
      const isActivelyFilling = currentFill > 0 && currentFill < 1;
      const rotation = isActivelyFilling ? currentFill * 360 : 0;
      return <div key={service.id} className="flex flex-col gap-5 transition-all duration-700 pointer-events-none" style={{
        flex: isActivelyFilling || idx === activeIndex ? "3" : "1"
      }}><div className={`transition-all duration-300 transform-gpu w-fit origin-center ${finalPercent > 0 || idx === activeIndex ? "text-white" : "text-zinc-400 opacity-40"}`} style={{
          transform: `rotate(${rotation}deg)`
        }}><Icon size={18} className="block" /></div><div className="relative w-full lg:h-1.5 md:h-1 h-0.5 bg-zinc-800/50 rounded-full overflow-hidden"><motion.div className="absolute top-0 left-0 h-full bg-white" initial={false} animate={{
            width: `${finalPercent * 100}%`
          }} transition={{
            duration: 0.1,
            ease: "linear"
          }} /></div></div>;
    });
    $[9] = activeIndex;
    $[10] = globalProgress;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <div className="relative z-50 w-full"><Container className="px-4 lg:px-10"><div className="flex w-full gap-4 items-end">{t6}</div></Container></div>;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="flex-1" aria-hidden={true} />;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t7) {
    t9 = <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col">{t3}{t4}{t5}{t7}{t8}</div>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t2 || $[19] !== t9) {
    t10 = <section ref={containerRef} id="services" className="relative w-full bg-accent-foreground lg:pt-8 min-h-[150vh] rounded-b-4xl" style={t2}>{t9}</section>;
    $[18] = t2;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
export default Services;